<script lang="ts">
	/**
	 * The credential form for one payment provider, generated from the adapter's own
	 * `describeCredentials()` (Design §5.1). Adding an adapter therefore adds **no** UI work: it
	 * declares its fields, and this renders them.
	 *
	 * Two rules do the real work here:
	 *
	 * - **Secrets are write-only.** A read returns a mask, never a value, so every secret field
	 *   starts empty with its mask as the placeholder.
	 * - **Every field is submitted on every save.** An untouched field submits the sentinel
	 *   `"unchanged"`, so the server keeps the stored ciphertext byte for byte. Submitting an
	 *   empty string instead — the obvious implementation — silently blanks a live credential on
	 *   an unrelated rename, and the store fails at the next checkout with nothing pointing at the
	 *   edit that caused it.
	 */

	import SelectField from './SelectField.svelte';
	import { Badge, Button, Input, Toggle } from 'flowbite-svelte';
	import { createEventDispatcher } from 'svelte';
	import type {
		CredentialFieldSpec,
		PaymentProviderConfig,
		PaymentProviderWriteInput,
		ProviderTypeDescriptor,
		WebhookProvisionResult
	} from '../AppTypes';
	import { UNCHANGED_SECRET } from '../AppTypes';
	import { provisionPaymentProviderWebhook } from '../AppNetwork';
	import ConfirmButton from './ConfirmButton.svelte';
	import FormField from './FormField.svelte';
	import Panel from './Panel.svelte';
	import RelativeDate from './RelativeDate.svelte';
	import { toastError, toastSuccess } from '../stores/toast';

	/** The row being edited, or null to render the create form. */
	export let provider: PaymentProviderConfig | null = null;
	/** Adapter types from `GET /api/admin/payment-provider/types`. */
	export let types: ProviderTypeDescriptor[] = [];
	export let saving: boolean = false;
	export let verifying: boolean = false;
	export let promoting: boolean = false;

	const dispatch = createEventDispatcher<{
		save: { input: PaymentProviderWriteInput };
		verify: Record<string, never>;
		makeDefault: Record<string, never>;
		cancel: Record<string, never>;
	}>();

	let selectedType: string = '';
	let name: string = '';
	let mode: 'test' | 'live' = 'test';
	let isActive: boolean = true;
	/** Field name → what the operator typed. Empty plus untouched means "keep what is stored". */
	let credentials: Record<string, string> = {};
	let options: Record<string, boolean | string> = {};
	/** Field name → whether it was edited, so an emptied optional field can actually be cleared. */
	let touched: Record<string, boolean> = {};

	let loadedFrom: PaymentProviderConfig | null | undefined = undefined;

	$: if (provider !== loadedFrom) {
		loadedFrom = provider;
		selectedType = provider?.type ?? types[0]?.type ?? '';
		name = provider?.name ?? '';
		mode = provider?.mode ?? 'test';
		isActive = provider?.isActive ?? true;
		credentials = {};
		touched = {};
		// Options are readable, unlike credentials, so the form shows what is stored and falls
		// back to the adapter's declared default for anything the row has not set.
		options = {};
		for (const option of types.find(candidate => candidate.type === (provider?.type ?? types[0]?.type ?? ''))?.optionFields ?? []) {
			const stored = provider?.options?.[option.name];
			options[option.name] =
				typeof stored === 'boolean' || typeof stored === 'string' ? stored : option.default;
		}
	}

	$: isCreate = provider === null;
	$: descriptor = types.find(candidate => candidate.type === selectedType) ?? null;
	$: fields = descriptor?.credentialFields ?? [];
	$: optionFields = descriptor?.optionFields ?? [];

	/** Handlers live here rather than inline: a Svelte template expression is not TypeScript. */
	function fnSetOptionFlag(name: string, event: Event): void {
		options[name] = (event.target as HTMLInputElement).checked;
	}

	function fnSetOptionText(name: string, event: Event): void {
		options[name] = (event.target as HTMLInputElement).value;
	}

	/**
	 * A row whose adapter this build no longer registers. Its fields cannot be rendered, so the
	 * form says so instead of showing an empty credential section that looks like "nothing to set".
	 */
	$: isUnknownType = provider !== null && descriptor === null;

	function maskOf(field: CredentialFieldSpec): string {
		return provider?.credentialMasks?.[field.name] ?? '';
	}

	function placeholderOf(field: CredentialFieldSpec): string {
		if (isCreate) {
			return field.placeholder ?? '';
		}
		const mask = maskOf(field);
		return mask.length > 0 ? mask : 'Not set';
	}

	/** What this field contributes to the payload. */
	function submissionValue(field: CredentialFieldSpec): string {
		const typed = credentials[field.name] ?? '';
		if (isCreate) {
			return typed;
		}
		// Untouched means untouched — not empty. The server preserves the stored ciphertext.
		return touched[field.name] ? typed : UNCHANGED_SECRET;
	}

	function fieldError(field: CredentialFieldSpec): string {
		if (!field.isRequired) {
			return '';
		}
		if (isCreate && (credentials[field.name] ?? '').length === 0) {
			return 'Required.';
		}
		if (!isCreate && touched[field.name] && (credentials[field.name] ?? '').length === 0) {
			return 'Required. Clear the field only if you are replacing it.';
		}
		return '';
	}

	$: credentialErrors = fields.some(field => fieldError(field).length > 0);
	$: canSave = !saving && name.trim().length > 0 && selectedType.length > 0 && !credentialErrors;

	type BadgeTone = 'green' | 'red' | 'yellow';

	const HEALTH_TONE: Record<string, BadgeTone> = {
		healthy: 'green',
		unhealthy: 'red',
		undecryptable: 'red',
		unknown: 'yellow'
	};

	/** Status is carried by the text, never by the colour alone. */
	const HEALTH_LABEL: Record<string, string> = {
		healthy: 'Credentials verified',
		unhealthy: 'Verification failed',
		undecryptable: 'Credentials cannot be decrypted',
		unknown: 'Not verified yet'
	};

	function fnSave(): void {
		if (!canSave) {
			return;
		}
		const payload: Record<string, string> = {};
		for (const field of fields) {
			payload[field.name] = submissionValue(field);
		}

		const input: PaymentProviderWriteInput = {
			name: name.trim(),
			mode,
			isActive,
			credentials: payload,
			options: { ...options }
		};
		if (isCreate) {
			input.type = selectedType;
		}

		dispatch('save', { input });
	}

	let provisioning: boolean = false;
	let provisionResult: WebhookProvisionResult | null = null;

	/**
	 * Points the provider's webhook at this store.
	 *
	 * The event list comes from the server, derived from the build's own parser map, so it cannot
	 * drift from what the store actually handles — which is the failure a hand-ticked list in a
	 * dashboard produces silently.
	 */
	async function fnProvisionWebhook(): Promise<void> {
		if (!provider) return;

		provisioning = true;
		provisionResult = null;
		try {
			const result = await provisionPaymentProviderWebhook(provider.uuid);
			provisionResult = result;
			toastSuccess(
				result.created ? 'Webhook endpoint created.' : 'Webhook endpoint brought up to date.'
			);
			dispatch('provisioned');
		} catch (error) {
			toastError('The webhook was not configured.', (error as Error).message);
		} finally {
			provisioning = false;
		}
	}

	async function fnCopyWebhookURL(): Promise<void> {
		if (!provider?.webhookURL) {
			return;
		}
		try {
			await navigator.clipboard.writeText(provider.webhookURL);
			toastSuccess('Webhook URL copied.');
		} catch {
			toastError('Could not copy the webhook URL. Select it and copy manually.');
		}
	}
</script>

<Panel faIcon="credit-card" title={isCreate ? 'New payment provider' : provider?.name}>
	<svelte:fragment slot="titleRight">
		{#if provider}
			<!-- Mode is a badge, not a field buried in the form: confusing a live provider for a test
			     one is the mistake that takes real money against test keys. -->
			<Badge color={provider.mode === 'live' ? 'red' : 'blue'}>
				{provider.mode === 'live' ? 'LIVE' : 'TEST'}
			</Badge>
			{#if provider.isDefault}
				<Badge color="green">Default</Badge>
			{/if}
			{#if !provider.isActive}
				<Badge color="dark">Inactive</Badge>
			{/if}
		{/if}
	</svelte:fragment>

	<form class="space-y-4" on:submit|preventDefault={fnSave}>
		<div class="grid md:grid-cols-2 gap-4">
			<FormField id="provider-type" label="Adapter" required helpText="Which payment provider this row talks to. It cannot be changed later. Create a second provider and promote it instead.">
				{#if isCreate}
					<SelectField
						id="provider-type"
						bind:value={selectedType}
						disabled={saving}
						items={types.map(type => ({ value: type.type, name: type.displayName }))}
						placeholder="Choose an adapter"
					/>
				{:else}
					<Input id="provider-type" value={descriptor?.displayName ?? provider?.type ?? ''} disabled />
				{/if}
			</FormField>

			<FormField id="provider-name" label="Name" required description="Admin-facing label, e.g. “Stripe (live)”.">
				<Input id="provider-name" bind:value={name} disabled={saving} placeholder="Stripe (live)" />
			</FormField>

			<FormField id="provider-mode" label="Mode" required description="Whether this row talks to the provider's test or live environment.">
				<SelectField
					id="provider-mode"
					bind:value={mode}
					disabled={saving}
					items={[
						{ value: 'test', name: 'Test' },
						{ value: 'live', name: 'Live' }
					]}
				/>
			</FormField>

			<FormField id="provider-active" label="Active" description="Inactive stops new checkouts. Existing subscriptions keep syncing.">
				<Toggle id="provider-active" bind:checked={isActive} disabled={saving}>
					{isActive ? 'Accepting new checkouts' : 'Not accepting new checkouts'}
				</Toggle>
			</FormField>
		</div>

		{#if isUnknownType}
			<div class="widget bg-depth text-sm">
				<p class="card-label mb-1">
					<i class="fa-solid fa-triangle-exclamation text-[11px] mr-1" />Unknown adapter
				</p>
				<p class="text-default">
					No adapter is registered for type <code>{provider?.type}</code> in this build, so its
					credential fields cannot be shown. The stored credentials are untouched; deploying a
					build that registers this adapter will restore the form.
				</p>
			</div>
		{:else}
			<div class="space-y-4">
				<p class="card-label">
					<i class="fa-solid fa-key text-[11px] mr-1" />Credentials
				</p>

				{#each fields as field (field.name)}
					{@const controlID = `provider-credential-${field.name}`}
					<FormField
						id={controlID}
						label={field.label}
						required={field.isRequired}
						helpText={field.help ?? ''}
						description={isCreate
							? ''
							: 'Leave blank to keep the stored value. Type a new one to replace it.'}
						error={fieldError(field)}
					>
						<Input
							id={controlID}
							type={field.isSecret ? 'password' : 'text'}
							autocomplete="off"
							placeholder={placeholderOf(field)}
							disabled={saving}
							bind:value={credentials[field.name]}
							on:input={() => (touched[field.name] = true)}
						/>
					</FormField>
				{/each}

				{#if fields.length === 0}
					<p class="text-sm text-default">This adapter declares no credential fields.</p>
				{/if}
			</div>

			{#if optionFields.length > 0}
				<!--
					Generated from the adapter's own `describeOptions()`, exactly like the credentials
					above, so a new adapter's switches appear with no client change. These are
					behaviour, not identity: readable, defaulted, and reachable — Stripe refuses
					automatic tax outright for accounts in unsupported countries, and an option only
					settable with `curl` would leave checkout broken with no way out of the admin UI.
				-->
				<div class="space-y-4 mt-6">
					<p class="card-label">
						<i class="fa-solid fa-sliders text-[11px] mr-1" />Behaviour
					</p>

					{#each optionFields as option (option.name)}
						{@const optionID = `provider-option-${option.name}`}
						<!-- `description`, not `helpText`: this guidance is always visible, because the
					     reason to switch an option off is usually a failing checkout, and an operator
					     hunting that does not know to hover a `?`. -->
						<FormField id={optionID} label={option.label} description={option.help ?? ''}>
							{#if option.type === 'boolean'}
								<Toggle
									id={optionID}
									disabled={saving}
									checked={options[option.name] === true}
									on:change={event => fnSetOptionFlag(option.name, event)}
								>
									{options[option.name] === true ? 'On' : 'Off'}
								</Toggle>
							{:else}
								<Input
									id={optionID}
									type="text"
									autocomplete="off"
									disabled={saving}
									value={String(options[option.name] ?? '')}
									on:input={event => fnSetOptionText(option.name, event)}
								/>
							{/if}
						</FormField>
					{/each}
				</div>
			{/if}
		{/if}

		<div class="flex flex-wrap items-center gap-3">
			<Button type="submit" color="primary" disabled={!canSave}>
				{#if saving}
					<i class="fa-solid fa-spinner fa-spin mr-2" />
				{/if}
				{isCreate ? 'Create provider' : 'Save changes'}
			</Button>

			<Button color="alternative" disabled={saving} on:click={() => dispatch('cancel', {})}>
				Cancel
			</Button>
		</div>
	</form>

	{#if provider}
		<hr class="my-5 border-gray-200 dark:border-gray-700" />

		<div class="grid lg:grid-cols-2 gap-4">
			<div class="widget bg-depth">
				<p class="card-label mb-2">
					<i class="fa-solid fa-stethoscope text-[11px] mr-1" />Credential health
				</p>

				<div class="flex flex-wrap items-center gap-2 mb-2">
					<Badge color={HEALTH_TONE[provider.healthStatus] ?? 'yellow'}>
						{HEALTH_LABEL[provider.healthStatus] ?? provider.healthStatus}
					</Badge>
					{#if provider.healthCheckedAt}
						<span class="text-xs text-default">
							Checked <RelativeDate date={provider.healthCheckedAt} size="xs" />
						</span>
					{/if}
				</div>

				{#if provider.healthError}
					<p class="text-xs text-red-600 dark:text-red-500 mb-2 break-words">{provider.healthError}</p>
				{/if}

				<p class="text-xs text-default mb-3">
					Verifying calls the provider with these credentials. A typo surfaces here rather than at
					a customer's checkout.
				</p>

				<Button size="sm" color="alternative" disabled={verifying} on:click={() => dispatch('verify', {})}>
					{#if verifying}
						<i class="fa-solid fa-spinner fa-spin mr-2" />
					{:else}
						<i class="fa-solid fa-circle-check mr-2" />
					{/if}
					Verify credentials
				</Button>
			</div>

			<div class="widget bg-depth">
				<p class="card-label mb-2">
					<i class="fa-solid fa-link text-[11px] mr-1" />Webhook URL
				</p>

				{#if provider.webhookURL}
					<p class="text-xs text-default mb-2">
						Configure it automatically, or paste it into the provider's dashboard by hand.
					</p>
					<div class="flex items-center gap-2">
						<Input value={provider.webhookURL} readonly size="sm" class="font-mono text-xs" />
						<Button size="sm" color="alternative" title="Copy webhook URL" on:click={fnCopyWebhookURL}>
							<i class="fa-solid fa-copy" />
						</Button>
					</div>

					<!-- Doing this by hand fails quietly in two ways: a mistyped signing secret makes
					     every inbound event fail verification, and a missed event type means that
					     event never arrives at all. Both look like nothing happening. -->
					<Button
						size="sm"
						color="alternative"
						class="mt-2"
						disabled={provisioning}
						on:click={fnProvisionWebhook}
					>
						<i class="fa-solid fa-wand-magic-sparkles mr-2" />
						{provisioning ? 'Configuring…' : 'Configure automatically'}
					</Button>

					{#if provisionResult !== null}
						<p class="text-xs text-default mt-2">
							{provisionResult.created ? 'Endpoint created' : 'Existing endpoint updated'}:
							{provisionResult.events.length} event types.
							{#if !provisionResult.secretStored}
								The signing secret is only shown when an endpoint is first created, so the one
								already stored here was kept. If it is wrong, delete the endpoint at the provider
								and run this again.
							{/if}
						</p>
					{/if}
				{:else}
					<p class="text-sm text-default">
						Set <strong>store.publicURL</strong> in Settings first. Until then there is no URL worth
						pasting into a provider dashboard.
					</p>
				{/if}
			</div>
		</div>

		{#if !provider.isDefault}
			<div class="mt-4">
				<ConfirmButton
					size="sm"
					color="primary"
					disabled={promoting || !provider.isActive}
					confirmText={`Make “${provider.name}” the default provider? All new purchases will be processed by it.`}
					on:confirm={() => dispatch('makeDefault', {})}
				>
					{#if promoting}
						<i class="fa-solid fa-spinner fa-spin mr-2" />
					{:else}
						<i class="fa-solid fa-star mr-2" />
					{/if}
					Make default
				</ConfirmButton>

				{#if !provider.isActive}
					<p class="text-xs text-default mt-1">Activate the provider before making it the default.</p>
				{/if}
			</div>
		{/if}
	{/if}
</Panel>
